import { ANSWER_KIND_VALUES, type AnswerKind } from '@/contracts/marketplace/reviews';

export type { AnswerKind };

export const ANSWER_KINDS = {
    human: 'human',
    template: 'template',
    aiUnchanged: 'ai_unchanged',
    aiChanged: 'ai_changed',
} as const satisfies Record<string, AnswerKind>;

export type AnswerKindCounts = {
    human: number;
    template: number;
    aiUnchanged: number;
    aiChanged: number;
};

const ANSWER_KIND_SET = new Set<string>(ANSWER_KIND_VALUES);

export const isAnswerKind = (value: unknown): value is AnswerKind =>
    typeof value === 'string' && ANSWER_KIND_SET.has(value);

export const normalizeAnswerKind = (value: unknown): AnswerKind => (isAnswerKind(value) ? value : ANSWER_KINDS.human);

export const createEmptyAnswerKindCounts = (): AnswerKindCounts => ({
    human: 0,
    template: 0,
    aiUnchanged: 0,
    aiChanged: 0,
});

export const addAnswerKindQuantity = (
    totals: AnswerKindCounts,
    answerKind: AnswerKind,
    quantity: number,
): AnswerKindCounts => {
    if (answerKind === ANSWER_KINDS.template) {
        return {
            ...totals,
            template: totals.template + quantity,
        };
    }

    if (answerKind === ANSWER_KINDS.aiUnchanged) {
        return {
            ...totals,
            aiUnchanged: totals.aiUnchanged + quantity,
        };
    }

    if (answerKind === ANSWER_KINDS.aiChanged) {
        return {
            ...totals,
            aiChanged: totals.aiChanged + quantity,
        };
    }

    return {
        ...totals,
        human: totals.human + quantity,
    };
};

export const getAnswerKindRollupCounterField = (
    answerKind: AnswerKind,
): 'humanAnswerCount' | 'templateAnswerCount' | 'aiAnswerUnchangedCount' | 'aiAnswerChangedCount' => {
    if (answerKind === ANSWER_KINDS.template) {
        return 'templateAnswerCount';
    }

    if (answerKind === ANSWER_KINDS.aiUnchanged) {
        return 'aiAnswerUnchangedCount';
    }

    if (answerKind === ANSWER_KINDS.aiChanged) {
        return 'aiAnswerChangedCount';
    }

    return 'humanAnswerCount';
};

export const getAiUnchangedAnswerPercent = (
    totals: Pick<AnswerKindCounts, 'aiUnchanged' | 'aiChanged'>,
): number | null => {
    const aiAnswerTotal = totals.aiUnchanged + totals.aiChanged;
    if (aiAnswerTotal <= 0) {
        return null;
    }

    return (totals.aiUnchanged / aiAnswerTotal) * 100;
};
